// Console-side store for the AI add-ons admin surface. Mirrors the shape
// of `usePlansStore` — both back the same dialog conventions in this repo.
export const useAddonsStore = defineStore('addons', {
  state: () => {
    return {
      total: 0,
      currentPage: 1,
      perPage: 100,
      addons: [],
      ai: {
        enabled: false, mcpEnabled: false, credits: [],
      },
    };
  },

  actions: {
    async fetchAddons(params) {
      const response = await useApi.get('/addons', { params }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      if (response) {
        this.total = response.total;
        this.addons = response.results;
        if (response.ai) {
          this.ai = response.ai;
        }
      }
    },

    async fetchAddon({ addonId, workspaceDomain }) {
      const response = await useApi.get(`/addons/${addonId}`, {
        params: { workspace: workspaceDomain },
      }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async create(body) {
      const response = await useApi.post('/addons', { body }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async update({ addonId, body }) {
      const response = await useApi.patch(`/addons/${addonId}`, { body }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async deactivate({ addonId, workspaceDomain }) {
      const response = await useApi.post(`/addons/${addonId}/deactivate`, {
        params: {
          workspace: workspaceDomain,
        },
      }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async setAiEnabled({ workspaceDomain, enabled }) {
      const response = await useApi.post('/addons/ai/enabled', {
        body: { workspace: workspaceDomain, enabled },
      }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async setMcpEnabled({ workspaceDomain, enabled }) {
      const response = await useApi.post('/addons/ai/mcp-enabled', {
        body: { workspace: workspaceDomain, enabled },
      }).catch((error) => {
        console.error(error);
        // 409 is the server refusing an opt-in while AI is unreachable. The
        // dialog disables the switch in that state, so seeing this means the
        // gate closed underneath the click — say which gate, not "went wrong".
        if (error?.status === 409) {
          useToast.error(useT('addon.mcpEnabled.aiRequired'));
        } else {
          useToast.error(useT('bcd270'));
        }
      });

      return response;
    },

    async setAiEnabledTo({ workspaceDomain, enabledTo }) {
      const response = await useApi.post('/addons/ai/enabled-to', {
        body: { workspace: workspaceDomain, enabledTo },
      }).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    // Takes the server's credit sets from a credit writer's answer, and only
    // those. The AI, per-user and MCP switches save on their own and may have
    // answered after this request left, so taking the whole `ai` block from it
    // could redraw a switch in the state it had before.
    mergeCredits(ai) {
      this.ai = { ...this.ai, credits: ai.credits || [] };
    },

    // Every credit writer answers with the whole `ai` block, so the store takes
    // the server's sets rather than patching one number into its own. The
    // array can differ by more than the edit: the interface drops expired sets
    // on its own reads, and a stale local copy would redraw them.
    // `expectedLeft` is the balance the dialog showed: the server writes only
    // while the set still holds it, so credits spent meanwhile are not undone.
    async adjustCreditSet({ workspaceDomain, receivedAt, left, expectedLeft }) {
      const response = await useApi.patch('/addons/ai/credits', {
        body: {
          workspace: workspaceDomain, receivedAt, left, expectedLeft,
        },
      }).catch((error) => {
        console.error(error);
        // 404 and 409 both mean the set the dialog drew is no longer the set on
        // disk, which a generic failure toast would hide behind "try again".
        // The sets are read again, or reopening the dialog would offer the same
        // stale balance and fail the same way.
        if (error?.status === 404 || error?.status === 409) {
          useToast.error(useT('addon.credits.staleSet'));
          this.fetchAddons({ workspaceDomain });
        } else {
          useToast.error(useT('bcd270'));
        }
      });

      if (response?.ai) {
        this.mergeCredits(response.ai);
      }

      return response;
    },

    async removeCreditSet({ workspaceDomain, receivedAt, expectedLeft }) {
      const response = await useApi.post('/addons/ai/credits/remove', {
        body: { workspace: workspaceDomain, receivedAt, expectedLeft },
      }).catch((error) => {
        console.error(error);
        if (error?.status === 404 || error?.status === 409) {
          useToast.error(useT('addon.credits.staleSet'));
          this.fetchAddons({ workspaceDomain });
        } else {
          useToast.error(useT('bcd270'));
        }
      });

      if (response?.ai) {
        this.mergeCredits(response.ai);
      }

      return response;
    },
  },
});
